import React, { useState, useEffect } from 'react';
import { useData } from '../../context/DataContext';
import { Search, GraduationCap, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrincipalStudents() {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sections, setSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState('ALL');

    useEffect(() => {
        // Fetch students and sections
        const fetchData = async () => {
            try {
                const [stuRes, secRes] = await Promise.all([
                    fetch('/api/students'),
                    fetch('/api/sections')
                ]);

                const stuData = await stuRes.json();
                const secData = await secRes.json();

                setStudents(stuData);
                setSections(secData);
                setLoading(false);
            } catch (e) {
                console.error(e);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const getFilteredStudents = () => {
        let filtered = students;

        if (selectedSection !== 'ALL') {
            filtered = filtered.filter(s => s.sectionId.toString() === selectedSection);
        }

        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            filtered = filtered.filter(s =>
                s.name.toLowerCase().includes(lower) ||
                s.rollNumber.toLowerCase().includes(lower)
            );
        }
        return filtered;
    };

    const displayStudents = getFilteredStudents();

    if (loading) return <div className="p-8">Loading student directory...</div>;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-primary">Student Directory</h1>
                    <p className="text-muted-foreground">Manage Student Records ({students.length} Total)</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 bg-card p-4 rounded-xl border border-border shadow-sm">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search by name or roll number..."
                        className="w-full pl-9 pr-4 py-2 bg-secondary/50 border-none rounded-lg focus:ring-1 focus:ring-primary outline-none"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="flex items-center gap-2 min-w-[200px]">
                    <Filter size={18} className="text-muted-foreground" />
                    <select
                        className="w-full bg-secondary/50 border-none rounded-lg px-3 py-2 text-sm"
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                    >
                        <option value="ALL">All Sections</option>
                        {sections.map(sec => (
                            <option key={sec.id} value={sec.id}>{sec.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-secondary/50 text-muted-foreground font-medium border-b border-border">
                            <tr>
                                <th className="p-4 w-20 text-center">#</th>
                                <th className="p-4">Student Name</th>
                                <th className="p-4">Roll Number</th>
                                <th className="p-4">Section</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {displayStudents.map((student, i) => {
                                const sectionName = sections.find(s => s.id === student.sectionId)?.name || 'Unknown';
                                return (
                                    <tr
                                        key={student.id}
                                        onClick={() => navigate(`/dashboard/students/${student.id}`)}
                                        className="hover:bg-muted/5 transition-colors group cursor-pointer"
                                    >
                                        <td className="p-4 text-center">
                                            <div className="h-8 w-8 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center font-bold text-xs">
                                                {student.name[0]}
                                            </div>
                                        </td>
                                        <td className="p-4 font-medium text-foreground">{student.name}</td>
                                        <td className="p-4 font-mono text-muted-foreground">{student.rollNumber}</td>
                                        <td className="p-4">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-medium">
                                                <GraduationCap size={12} />
                                                {sectionName}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {displayStudents.length === 0 && (
                    <div className="p-8 text-center text-muted-foreground">
                        No students found matching your search.
                    </div>
                )}
            </div>
        </div>
    );
}
